import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { devLog, devError, devTime } from '@/lib/debug';

export type UserStatus = 'loading' | 'authenticated' | 'anonymous' | 'error';

interface UserContextValue {
  user: User | null;
  session: Session | null;
  status: UserStatus;
  error: Error | null;
  refresh: () => Promise<void>;
  isAdmin: boolean;
  isSeller: boolean;
  companyId: string | null;
}

// Exported for testing/guard utilities - prefer using useCurrentUser() hook
export const UserContext = createContext<UserContextValue | null>(null);

// ============================================
// MODULE GRAPH HARDENING (duplicate-context detection)
// ============================================
// If the bundle ever loads two copies of this module (e.g. stale SW caching mixing old/new
// chunks, or a path/casing mismatch), React context identity will differ between provider
// and consumer. That can surface as:
//   "useCurrentUser called outside UserProvider"
// even when the provider tree is correct.
//
// We use a process-wide singleton key to PROVE whether multiple context identities exist.
// (Works in production too.)
const KEY = Symbol.for("LC_USER_CONTEXT_SINGLETON");

const g = globalThis as any;
if (!g[KEY]) {
  g[KEY] = UserContext;
} else if (g[KEY] !== UserContext) {
  // Definitive proof of duplication: two different createContext() identities exist.
  g.__LC_USER_CONTEXT_DUPLICATE__ = true;
  console.error("❌ [UserContext] MULTIPLE UserContext INSTANCES DETECTED", {
    first: g[KEY],
    current: UserContext,
    hint: "This indicates duplicate module graphs (e.g. stale SW / mixed chunks / path mismatch).",
  });
}



/**
 * Provider that centralizes user authentication state.
 * Calls supabase.auth.getUser() only ONCE on mount, then listens to auth changes.
 * All components use useCurrentUser() instead of calling getUser() directly.
 */
export function UserProvider({ children }: { children: React.ReactNode }) {
  // Diagnostic flag used to distinguish provider-outside-tree vs duplicated-context.
  useEffect(() => {
    (globalThis as any).__LC_USER_PROVIDER_MOUNTED__ = true;
  }, []);

  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<UserStatus>('loading');
  const [error, setError] = useState<Error | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSeller, setIsSeller] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);
  
  const initializedRef = useRef(false);
  const rolesLoadedRef = useRef(false);

  const loadUserRoles = useCallback(async (userId: string) => {
    if (rolesLoadedRef.current) return;
    rolesLoadedRef.current = true;
    
    try {
      // Batch both queries in parallel
      const [rolesResult, companyResult] = await Promise.all([
        supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId),
        supabase
          .from('companies')
          .select('id')
          .eq('owner_id', userId)
          .maybeSingle()
      ]);

      setIsAdmin(rolesResult.data?.some(r => r.role === 'admin') || false);
      setIsSeller(!!companyResult.data);
      setCompanyId(companyResult.data?.id || null);
    } catch (err) {
      devError('UserContext', 'Error loading user roles:', err);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      setStatus('loading');
      const { data: { user: currentUser }, error: authError } = await supabase.auth.getUser();
      
      if (authError) throw authError;
      
      setUser(currentUser);
      setStatus(currentUser ? 'authenticated' : 'anonymous');
      setError(null);
      
      if (currentUser) {
        rolesLoadedRef.current = false;
        await loadUserRoles(currentUser.id);
      } else {
        setIsAdmin(false);
        setIsSeller(false);
        setCompanyId(null);
      }
    } catch (err) {
      devError('UserContext', 'Error refreshing user:', err);
      setError(err as Error);
      setStatus('error');
    }
  }, [loadUserRoles]);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const endTimer = devTime('UserContext Initial auth load');

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        devLog('UserContext', 'Auth state change:', event);
        setSession(newSession);
        setUser(newSession?.user ?? null);
        setStatus(newSession?.user ? 'authenticated' : 'anonymous');
        
        if (newSession?.user) {
          rolesLoadedRef.current = false;
          // Defer role loading to avoid Supabase deadlock
          setTimeout(() => {
            loadUserRoles(newSession.user.id);
          }, 0);
        } else {
          setIsAdmin(false);
          setIsSeller(false);
          setCompanyId(null);
          rolesLoadedRef.current = false;
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session: existingSession }, error: sessionError }) => {
      endTimer();
      
      if (sessionError) {
        devError('UserContext', 'Error getting session:', sessionError);
        setError(sessionError);
        setStatus('error');
        return;
      }

      setSession(existingSession);
      setUser(existingSession?.user ?? null);
      setStatus(existingSession?.user ? 'authenticated' : 'anonymous');

      if (existingSession?.user) {
        loadUserRoles(existingSession.user.id);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [loadUserRoles]);

  return (
    <UserContext.Provider value={{ 
      user, 
      session, 
      status, 
      error, 
      refresh,
      isAdmin,
      isSeller,
      companyId
    }}>
      {children}
    </UserContext.Provider>
  );
}

/**
 * Default value returned when useCurrentUser is called outside UserProvider.
 * This prevents production crashes while still logging an error for debugging.
 */
const FALLBACK_USER_CONTEXT: UserContextValue = {
  user: null,
  session: null,
  status: 'anonymous',
  error: new Error('UserProvider not mounted'),
  refresh: async () => {
    console.warn('[UserContext] refresh() called but UserProvider is not mounted');
  },
  isAdmin: false,
  isSeller: false,
  companyId: null
};

// Track if we've already logged the provider error (avoid spam)
let providerErrorLogged = false;

/**
 * Hook to access the current user. 
 * ALWAYS use this instead of supabase.auth.getUser() in components.
 * 
 * PRODUCTION SAFETY: If context is missing (component rendered outside provider),
 * this hook returns a safe default instead of crashing the entire app.
 * In development, it throws to help catch bugs early.
 */
export function useCurrentUser(): UserContextValue {
  const context = useContext(UserContext);
  
  if (!context) {
    // In development: throw loudly to catch bugs early
    if (import.meta.env.DEV) {
      // Create error with stack trace for better debugging
      const error = new Error(
        'useCurrentUser must be used within a UserProvider. ' +
        'Ensure UserProvider is at the top of your component tree in main.tsx.'
      );
      console.error('❌ [UserContext] Provider missing!', error.stack);
      throw error;
    }
    
    // In production: log diagnostics + stack trace but don't crash
    if (!providerErrorLogged) {
      providerErrorLogged = true;
      const error = new Error('[UserContext] useCurrentUser called outside UserProvider');

      const providerMounted = !!(globalThis as any).__LC_USER_PROVIDER_MOUNTED__;
      const duplicateDetected = !!(globalThis as any).__LC_USER_CONTEXT_DUPLICATE__;

      console.error(
        '❌ [UserContext] CRITICAL: useCurrentUser called outside UserProvider.',
        '\nDiagnostics:',
        { providerMounted, duplicateDetected },
        '\nStack trace:',
        error.stack
      );

      // Attempt to capture to telemetry if available (e.g., Sentry)
      if (typeof window !== 'undefined' && (window as any).Sentry?.captureException) {
        (window as any).Sentry.captureException(error);
      }
    }
    
    return FALLBACK_USER_CONTEXT;
  }
  
  return context;
}

/**
 * Safe version that returns null when outside provider (for optional usage).
 * Use this when you're not sure if the component will be inside UserProvider.
 */
export function useCurrentUserSafe(): UserContextValue | null {
  return useContext(UserContext);
}

/**
 * Runtime assertion to verify UserProvider is mounted.
 * Call this once at app startup to catch configuration errors early.
 */
export function assertUserProviderMounted(): void {
  // This is called from a useEffect, so we need to check if context exists
  // by trying to get the provider value from a test render
  if (typeof window !== 'undefined') {
    // Check after a microtask to ensure React has mounted
    queueMicrotask(() => {
      const rootEl = document.getElementById('root');
      if (rootEl) {
        const textContent = rootEl.textContent || '';
        if (textContent.includes('must be used within a UserProvider')) {
          console.error(
            '❌ [STARTUP ASSERTION FAILED] UserProvider is NOT mounted correctly!',
            '\nThis will cause auth context errors throughout the app.',
            '\nEnsure UserProvider wraps <App /> in main.tsx.'
          );
        }
      }
    });
  }
}
